<script setup lang="ts">
import DashboardView from ".//DashboardView.vue";
import { onBeforeMount, ref, computed, onBeforeUnmount, provide } from "vue";
import { useRoute } from "vue-router";
import Header from "../components/Headerbar.vue";
import { useApiClient } from "@/api/client";
import { useDashboardStore } from "@/modules/dashboards/store";
import router from "@/router/router";
import { useBaseStore } from "@/stores/base";
import MainBreadcrumbs from "@/ui/components/header/MainBreadcrumbs.vue";
import { useExplorerSecondaryDrawer } from "@/modules/explorer/composables/ExplorerSecondaryDrawer";
const { openSecondaryDrawer } = useExplorerSecondaryDrawer();

provide("fetchDataPrefix", "dashboard/");

const dashboardState = useDashboardStore().dashboardState;
const dashboard_uuid: string | string[] =
	useRoute().params.dashboard_uuid !== ""
		? String(useRoute().params.dashboard_uuid)
		: null;

dashboardState.isFullscreen = !!(
	router.currentRoute.value.query["fullscreen"] ?? false
);

const client = useApiClient("dashboard");
const loading = ref(false);

const getDashboard = async () => {
	loading.value = true;
	const dashboardResponse = await client.get(dashboard_uuid);
	if (dashboardResponse.isOk()) {
		dashboardState.dashboard = dashboardResponse.value;
	}
	loading.value = false;
};

onBeforeMount(async () => {
	useBaseStore().appFullWidth = true;
	await getDashboard();

	const { "new-document": newDocument } = router.currentRoute.value
		.query as Record<string, string>;

	if (!newDocument) {
		dashboardState.pendingNewCard = null;
		return;
	}

	const pendingNewCard = dashboardState.pendingNewCard;
	const card = pendingNewCard?.card ?? null;
	const slot = pendingNewCard?.slot ?? "document";

	dashboardState.pendingNewCard = {
		card,
		slot,
		documentUuid: newDocument,
	};

	await router.replace({ query: null });
	openSecondaryDrawer("AddEditCard");
});

onBeforeUnmount(() => {
	useBaseStore().appFullWidth = false;
});

const computedBreadcrumbs = computed(() => {
	const breadcrumbs = [
		{
			title: "Dashboard",
			disabled: false,
			to: "/dashboards",
		},
	];

	if (dashboardState?.dashboard?.title) {
		breadcrumbs.push({
			title: dashboardState.dashboard.title,
			disabled: true,
			to: "/dashboards",
		});
	}

	return breadcrumbs;
});
</script>

<template>
	<main-breadcrumbs :breadcrumbs="computedBreadcrumbs" />
	<v-overlay
		v-if="loading"
		v-model="loading"
		:close-on-content-click="false"
		style="display: grid; place-items: center"
		persistent
		scrim="#cccccc"
		contained
	>
		<v-progress-circular
			close-delay="0"
			style="width: 100%"
			color="primary"
			height="6"
			indeterminate
			open-delay="0"
			rounded
		/>

		<v-label
			class="text-subtitle-1 font-weight-semibold pb-2 text-primary justify-center w-100"
			>Loading data
		</v-label>
	</v-overlay>
	<div v-else class="pb-15">
		<!-- Page break in edit mode has a z-index of 999 -->
		<Header class="flex-grow-0" style="z-index: 1005" />
		<DashboardView class="flex-grow-1" />
	</div>
</template>
